import * as React from 'react'

import { Button } from '@/components/ui/button'
import { ChevronDown, Minus, Plus } from 'lucide-react'
import { ItemUnitEnum } from '@/gen/cortado/v1/settings_pb'
import { formatTaxRateFraction } from '@/lib/currency-utils'
import { formatEnumName } from '@/lib/enum-utils'
import { cn } from '@/lib/utils'

/**
 * Shared two-line row for every section of the quote builder's Line Items card
 * — primary line items, the transportation charge and the optional add-ons —
 * so the card reads as one continuous list at any width.
 *
 *   ⠿  Standard Package | 8 Hrs Beverage Service        $2,430.00  ⋯
 *      [−] 3 [+] · per event · Tax 9.15% — Denver     [ $ 810.00 ]
 *
 * Quantity and unit price are always live inline; name, description, unit and
 * tax are edited in the sheet behind the row's overflow menu.
 */

/**
 * Unit-price field sizing, shared so the field cannot drift. 144px fits
 * `$125,000.00` at this font once the `$` adornment's padding is accounted
 * for — a 112px field clipped its own value at 117px of content. Right
 * aligned and tabular so digits line up with the line total above and any
 * overflow eats the cents rather than the significant digits.
 */
export const QUOTE_ITEM_PRICE_INPUT_CLASS = 'h-8 w-36 text-right text-xs tabular-nums'

export function QuoteItemListContainer({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  // No `overflow-hidden`: it would clip the inline pickers' dropdowns, which
  // render inside this tree precisely so the Sheet's scroll lock cannot reach
  // them. Corners are rounded on the first and last child instead, which is
  // all the clipping was ever buying.
  return (
    <div
      className={cn(
        'rounded-xl border [&>*:first-child]:rounded-t-xl [&>*:last-child]:rounded-b-xl',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function QuoteItemList({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <ul className={cn('divide-y [&>li:first-child]:rounded-t-xl [&:last-child>li:last-child]:rounded-b-xl', className)}>
      {children}
    </ul>
  )
}

/**
 * @param quantityControl Stepper for editable quantities, muted text for
 * derived ones (mileage).
 */
export function QuoteItemTwoLineRow({
  dragHandle,
  name,
  badge,
  totalLabel,
  quantityControl,
  metaItems,
  priceControl,
  menu,
}: {
  dragHandle?: React.ReactNode
  name: string
  badge?: React.ReactNode
  /** Line total. Omitted when it would only repeat the unit-price field. */
  totalLabel?: string
  quantityControl: React.ReactNode
  /** Muted xs facts after the quantity, separated by `·`. Text or pickers. */
  metaItems: React.ReactNode[]
  priceControl?: React.ReactNode
  menu?: React.ReactNode
}) {
  const parts = metaItems.filter(Boolean)

  return (
    <div className="group flex items-start gap-2 px-3 py-2.5">
      {dragHandle}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <span className="min-w-0 flex-1 break-words text-sm font-medium">{name}</span>
          {totalLabel && (
            <span className="shrink-0 text-sm font-semibold tabular-nums">{totalLabel}</span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground">
            {quantityControl}
            {parts.map((part, index) => (
              <React.Fragment key={index}>
                <span aria-hidden="true">·</span>
                {typeof part === 'string' ? <span>{part}</span> : part}
              </React.Fragment>
            ))}
          </div>
          {priceControl}
        </div>
        {badge}
      </div>
      {menu}
    </div>
  )
}

/** Compact inline stepper sized for the muted second line. */
export function QuoteItemQuantityStepper({
  itemName,
  quantity,
  increment,
  disabled,
  onQuantityChange,
}: {
  itemName: string
  quantity: number
  increment: number
  disabled?: boolean
  onQuantityChange: (quantity: number) => void
}) {
  const atMinimum = quantity <= increment
  const round = (value: number) => Math.round(value * 10) / 10

  return (
    <span className="inline-flex items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-6"
        aria-label={`Decrease quantity for ${itemName}`}
        onClick={() => onQuantityChange(round(Math.max(increment, quantity - increment)))}
        disabled={disabled || atMinimum}
      >
        <Minus className="size-3" />
      </Button>
      <output
        aria-label={`Quantity for ${itemName}`}
        className="min-w-6 text-center text-xs tabular-nums text-foreground"
      >
        {formatQuoteItemQuantity(quantity)}
      </output>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-6"
        aria-label={`Increase quantity for ${itemName}`}
        onClick={() => onQuantityChange(round(quantity + increment))}
        disabled={disabled}
      >
        <Plus className="size-3" />
      </Button>
    </span>
  )
}

/** Read-only quantity for rows whose amount is derived (transportation mileage). */
export function QuoteItemDerivedQuantity({ label }: { label: string }) {
  return <span>× {label}</span>
}

export function formatQuoteItemQuantity(quantity: number): string {
  return quantity % 1 === 0 ? `${quantity}` : quantity.toFixed(1)
}

/** Per-unit step: half hours, tenths of a mile, whole units otherwise. */
export function getQuoteItemQuantityIncrement(unit: ItemUnitEnum): number {
  if (unit === ItemUnitEnum.ITEM_UNIT_PER_HOUR) return 0.5
  if (unit === ItemUnitEnum.ITEM_UNIT_PER_MILE) return 0.1
  return 1
}

export function formatQuoteItemUnitLabel(unit: ItemUnitEnum): string {
  return formatEnumName(unit, ItemUnitEnum).toLowerCase()
}

/** `Tax 9.15% — Denver` / `Tax 9.15%` / `Non-taxable`. */
export function formatQuoteItemTaxSubLine({
  isTaxable,
  salesTaxRate,
  salesTaxRateName,
}: {
  isTaxable: boolean
  salesTaxRate?: number
  salesTaxRateName?: string
}): string {
  if (!isTaxable || !salesTaxRate) return 'Non-taxable'
  const rate = `Tax ${formatTaxRateFraction(salesTaxRate)}%`
  return salesTaxRateName ? `${rate} — ${salesTaxRateName}` : rate
}

export function QuoteItemDragHandle({
  itemName,
  attributes,
  listeners,
  disabled,
}: {
  itemName: string
  attributes: React.HTMLAttributes<HTMLButtonElement>
  listeners?: Record<string, unknown>
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      {...attributes}
      {...listeners}
      aria-label={`Reorder ${itemName}`}
      className={cn(
        'mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        disabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab hover:text-foreground active:cursor-grabbing',
      )}
      disabled={disabled}
    >
      <GripDots />
    </button>
  )
}

function GripDots() {
  return (
    <svg viewBox="0 0 10 16" className="size-3.5 fill-current" aria-hidden="true">
      <circle cx="2" cy="3" r="1.3" /><circle cx="8" cy="3" r="1.3" />
      <circle cx="2" cy="8" r="1.3" /><circle cx="8" cy="8" r="1.3" />
      <circle cx="2" cy="13" r="1.3" /><circle cx="8" cy="13" r="1.3" />
    </svg>
  )
}

/** Tallest the inline picker's list may grow before it scrolls. */
export const QUOTE_ITEM_SELECT_MAX_HEIGHT_PX = 240

export type QuoteItemInlineOption = {
  value: string
  label: string
}

/**
 * Inline muted-text picker for the row's second line.
 *
 * Deliberately NOT a Popover/Select: the quote builder renders inside a Sheet,
 * and Radix's modal scroll lock blocks scroll events on Portal content because
 * the Portal is a DOM sibling of the Sheet rather than a child. A tax list
 * scrolls as soon as an org has more than a few rates, so this stays a plain
 * absolutely-positioned div inside the Sheet's own tree.
 * See `.claude/rules/frontend-patterns.md` and `tasks/entity-linker.tsx`.
 */
export function QuoteItemInlineSelect({
  label,
  triggerLabel,
  value,
  options,
  disabled,
  onSelect,
}: {
  /** Accessible name of the trigger, e.g. `Change unit for Espresso bar`. */
  label: string
  triggerLabel: string
  value: string
  options: QuoteItemInlineOption[]
  disabled?: boolean
  onSelect: (value: string) => void
}) {
  const [open, setOpen] = React.useState(false)
  const [placement, setPlacement] = React.useState<'bottom' | 'top'>('bottom')
  const containerRef = React.useRef<HTMLSpanElement | null>(null)
  const triggerRef = React.useRef<HTMLButtonElement | null>(null)
  const optionRefs = React.useRef<Array<HTMLButtonElement | null>>([])

  /**
   * Opens upward when the row sits near the bottom of the viewport, so a
   * last-row picker does not push the Sheet into scrolling. Falls back to
   * downward wherever there is no layout to measure (jsdom, SSR).
   */
  const openList = React.useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    const spaceBelow = rect ? window.innerHeight - rect.bottom : 0
    const spaceAbove = rect ? rect.top : 0
    setPlacement(
      spaceBelow < QUOTE_ITEM_SELECT_MAX_HEIGHT_PX && spaceAbove > spaceBelow ? 'top' : 'bottom',
    )
    setOpen(true)
  }, [])

  const close = React.useCallback((returnFocus = true) => {
    setOpen(false)
    if (returnFocus) triggerRef.current?.focus()
  }, [])

  React.useEffect(() => {
    if (!open) return
    const selectedIndex = Math.max(options.findIndex((option) => option.value === value), 0)
    optionRefs.current[selectedIndex]?.focus()
  }, [open, options, value])

  React.useEffect(() => {
    if (!open) return
    function handlePointerDown(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const moveFocus = (from: number, delta: number) => {
    const next = (from + delta + options.length) % options.length
    optionRefs.current[next]?.focus()
  }

  return (
    <span ref={containerRef} className="relative inline-flex">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={label}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            openList()
          }
        }}
        className={cn(
          'inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-xs text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-muted-foreground',
        )}
      >
        {triggerLabel}
        <ChevronDown className="size-3" aria-hidden="true" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 z-50 min-w-44 rounded-lg border bg-popover shadow-lg',
            placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1',
          )}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              event.stopPropagation()
              close()
            }
          }}
        >
          <ul
            role="listbox"
            aria-label={label}
            className="overflow-y-auto py-1"
            style={{ maxHeight: QUOTE_ITEM_SELECT_MAX_HEIGHT_PX }}
          >
            {options.map((option, index) => (
              <li key={option.value}>
                <button
                  ref={(element) => { optionRefs.current[index] = element }}
                  type="button"
                  role="option"
                  aria-selected={option.value === value}
                  onClick={() => {
                    onSelect(option.value)
                    close()
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'ArrowDown') {
                      event.preventDefault()
                      moveFocus(index, 1)
                    } else if (event.key === 'ArrowUp') {
                      event.preventDefault()
                      moveFocus(index, -1)
                    }
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm outline-none hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
                    option.value === value && 'font-medium',
                  )}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </span>
  )
}

/** `Non-taxable` sentinel for the inline tax picker. */
export const QUOTE_ITEM_NO_TAX_VALUE = 'none'

export function buildQuoteItemTaxOptions(
  taxRates: Array<{ id: string; name: string; percentage: number }>,
): QuoteItemInlineOption[] {
  return [
    { value: QUOTE_ITEM_NO_TAX_VALUE, label: 'Non-taxable' },
    ...taxRates.map((rate) => ({
      value: rate.id,
      label: `${rate.name} (${formatTaxRateFraction(rate.percentage / 100)}%)`,
    })),
  ]
}

export function buildQuoteItemUnitOptions(): QuoteItemInlineOption[] {
  return [
    ItemUnitEnum.ITEM_UNIT_PER_EVENT,
    ItemUnitEnum.ITEM_UNIT_PER_HOUR,
    ItemUnitEnum.ITEM_UNIT_PER_DAY,
    ItemUnitEnum.ITEM_UNIT_PER_UNIT,
  ].map((unit) => ({ value: String(unit), label: formatEnumName(unit, ItemUnitEnum) }))
}
